import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosConfig';
import '../pages/Profiles.css';

const NovedadesTramiteModal = ({ isOpen, tramiteId, onClose, onSuccess, readOnly = false }) => {
  const [novelties, setNovelties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    template_id: '',
    name: '',
    responsable: 'Concesionario',
    description: ''
  });

  const fetchNovelties = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/novelties/tramite/${tramiteId}`);
      setNovelties(res.data);
    } catch (err) {
      console.error(err);
      alert('❌ Error al cargar las novedades del trámite');
    } finally {
      setLoading(false);
    }
  }, [tramiteId]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get('/api/novelties/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (isOpen && tramiteId) {
      fetchNovelties();
      fetchTemplates();
    } else {
      setNovelties([]);
      setFormData({ template_id: '', name: '', responsable: 'Concesionario', description: '' });
    }
  }, [isOpen, tramiteId, fetchNovelties, fetchTemplates]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTemplateChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setFormData({
        template_id: 'custom',
        name: '',
        responsable: 'Concesionario',
        description: ''
      });
    } else {
      const selectedTemplate = templates.find(t => t.id === parseInt(val));
      setFormData({
        template_id: val,
        name: selectedTemplate ? selectedTemplate.name : '',
        responsable: selectedTemplate ? selectedTemplate.responsable : 'Concesionario',
        description: selectedTemplate ? selectedTemplate.description || '' : ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.template_id) {
      alert('⚠️ Seleccione una plantilla o novedad libre');
      return;
    }

    if (formData.template_id === 'custom' && (!formData.name.trim() || !formData.responsable)) {
      alert('⚠️ Por favor complete el nombre y el responsable para la novedad libre');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        template_id: formData.template_id === 'custom' ? null : formData.template_id,
        name: formData.name,
        responsable: formData.responsable,
        description: formData.description
      };

      await axios.post(`/api/novelties/tramite/${tramiteId}`, payload);
      alert('✅ Novedad registrada correctamente');
      setFormData({ template_id: '', name: '', responsable: 'Concesionario', description: '' });
      fetchNovelties();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo agregar la novedad'));
    } finally {
      setSaving(false);
    }
  };

  const resolveNovelty = async (id, name) => {
    if (!window.confirm(`⚠️ ¿Está seguro de confirmar que la novedad "${name}" ya está solucionada?`)) return;

    try {
      setSaving(true);
      await axios.put(`/api/novelties/instance/${id}/resolve`);
      alert('✅ Novedad marcada como solucionada');
      fetchNovelties();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo resolver la novedad'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>⚠️ Novedades del Trámite #{tramiteId}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: readOnly ? '1fr' : '1fr 1fr', gap: '20px', padding: '20px', alignItems: 'start' }}>
          {/* Listado de novedades */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '800', borderBottom: '1px solid var(--card-border)', paddingBottom: '8px' }}>
              📋 Registro de Incidencias
            </h4>

            {loading ? (
              <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Cargando novedades...</p>
            ) : novelties.length === 0 ? (
              <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-secondary)', border: '2px dashed var(--card-border)', borderRadius: '8px' }}>
                Sin novedades registradas
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '50vh', overflowY: 'auto', paddingRight: '5px' }}>
                {novelties.map((n) => (
                  <div key={n.id} style={{
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--card-border)',
                    background: n.status === 'pendiente' ? 'rgba(239, 68, 68, 0.05)' : 'rgba(16, 185, 129, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '800', fontSize: '13px', color: 'var(--text-primary)' }}>{n.name}</span>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '9px',
                        fontWeight: '900',
                        background: n.status === 'pendiente' ? '#fee2e2' : '#d1fae5',
                        color: n.status === 'pendiente' ? '#dc2626' : '#059669'
                      }}>
                        {n.status.toUpperCase()}
                      </span>
                    </div>

                    <p style={{ fontSize: '12px', margin: 0, color: 'var(--text-secondary)' }}>{n.description}</p>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '10px', color: '#94a3b8' }}>
                      <span>👤 Resp: <strong>{n.responsable.toUpperCase()}</strong></span>
                      <span>Creado por: @{n.creator_name || 'sistema'}</span>
                    </div>

                    {n.status === 'solucionada' && (
                      <div style={{ borderTop: '1px dashed var(--card-border)', paddingTop: '4px', marginTop: '4px', fontSize: '10px', color: '#059669' }}>
                        ✓ Solucionado por: @{n.resolver_name} ({new Date(n.resolved_at).toLocaleString()})
                      </div>
                    )}

                    {n.status === 'pendiente' && (
                      <button
                        onClick={() => resolveNovelty(n.id, n.name)}
                        disabled={saving}
                        className="btn-primary"
                        style={{
                          background: '#10b981',
                          color: '#fff',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          marginTop: '8px',
                          alignSelf: 'flex-start',
                          boxShadow: 'none'
                        }}
                      >
                        ✓ Confirmar Solucionada
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulario para agregar novedad */}
          {!readOnly && (
            <div style={{ padding: '15px', border: '1px solid var(--card-border)', borderRadius: '8px', background: 'var(--secondary-soft)' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '15px', color: 'var(--text-primary)' }}>
                ⚠️ Reportar Novedad
              </h4>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-secondary)' }}>
                    SELECCIONAR TIPO DE NOVEDAD
                  </label>
                  <select
                    name="template_id"
                    value={formData.template_id}
                    onChange={handleTemplateChange}
                    required
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                  >
                    <option value="">seleccione...</option>
                    <option value="custom" style={{ fontWeight: 'bold', color: '#2563eb' }}>
                      ✏️ Novedad Libre / Personalizada
                    </option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} (Resp: {t.responsable})
                      </option>
                    ))}
                  </select>
                </div>

                {formData.template_id === 'custom' && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-secondary)' }}>
                        NOMBRE DE LA NOVEDAD LIBRE <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Ej. Error en número de motor / Chasis ilegible"
                        required
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-secondary)' }}>
                        RESPONSABLE DE RESOLVER <span style={{ color: 'red' }}>*</span>
                      </label>
                      <select
                        name="responsable"
                        value={formData.responsable}
                        onChange={handleInputChange}
                        required
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                      >
                        <option value="Concesionario">Concesionario</option>
                        <option value="Operador">Operador</option>
                        <option value="Tránsito">Tránsito</option>
                        <option value="Validador">Validador</option>
                        <option value="Cliente">Cliente</option>
                      </select>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-secondary)' }}>
                    OBSERVACIONES / DETALLES
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Detalles sobre el problema y cómo solucionarlo..."
                    rows="4"
                    required
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', resize: 'vertical' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving || !formData.template_id}
                  className="btn-primary"
                  style={{
                    padding: '10px',
                    fontSize: '11px',
                    fontWeight: '800',
                    marginTop: '10px',
                    width: '100%'
                  }}
                >
                  {saving ? '⏳ GUARDANDO...' : '🚨 REGISTRAR NOVEDAD'}
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="modal-actions" style={{ padding: '15px 20px', borderTop: '1px solid var(--card-border)' }}>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
            Cerrar Ventana
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovedadesTramiteModal;
